/**
 * 狛江市医療・介護・地域資源マップ検索のトップレベル component
 *
 * * [App]{@link module:App~App}
 *     * [KomaeForm]{@link module:KomaeForm}
 *     * [KomaeResult]{@link module:KomaeResult}
 *
 * @module App
 *
 */
import React from 'react';
import KomaeForm from './KomaeForm';
import KomaeResult from './KomaeResult';

import * as KomaeConfig from './KomaeConfig';

/**
 * kokosil の [GET /api/v3/spots]{@link https://komae-iryoukaigotiiki-map.kokosil.net/ja/developer/document/api/v3/get_spots} による検索結果の [Spot]{@link https://komae-iryoukaigotiiki-map.kokosil.net/ja/developer/document/api/v3/resource/spot}。
 *
 * ここでは、表示・制御に利用している属性のみ抜粋して示す。詳細は上記リンク先の
 * ココシルのドキュメント参照のこと。
 *
 * @typedef SpotObject
 * @prop {string} name        - Spot の名称
 * @prop {string} description - Spot の説明
 * @prop {string} thumbnail   - サムネイル画像のURL
 * @prop {string} ucode       - Spot の場所 ucode
 *
 */

/**
 * 狛江市医療・介護・地域資源マップ検索のトップレベル component
 */
class App extends React.Component {
  constructor(props) {
    super(props);
    /**
     * 検索条件や検索結果を格納している。
     *
     * @member {Object}
     * @prop {string}  keyword     - 検索キーワード<br />
     *     kokosil の /api/v3/spots の keyword パラメータの値として利用される。
     * @prop {string}  categories  - 検索のカテゴリ条件パラメータ<br />
     *     kokosil の /api/v3/spots に渡す "`category2=1+2&categry2=3+4`" のよ
     *     うなパラメータ。（keyword とは違い、値だけではなくパラメータ名込みで
     *     の文字列）
     * @prop {Object[]} spots      - 検索結果のスポット一覧<br />
     *    検索結果の [SpotObject]{@link module:App~SpotObject} の ARRAY
     * @prop {number} offset      - 検索開始位置<br />
     *    /api/v3/spots に渡す offset パラメータとして使う。
     * @prop {boolean} hasNext     - 検索の続きの有無<br />
     *    /api/v3/spots で一定個数ごとに検索したときの、続きがあるかどうかのフラグ。
     * @prop {boolean} searcing    - 検索APIが実行中かどうかのフラグ
     * @prop {string}  searchError - 検索APIがエラーになったときのエラー文字列
     * @prop {string}  mode        - 画面モード。`form`、`result`、または `map`
     */

    // URLパラメータを見てフォーム画面か検索結果画面かを決める。
    const url = new URL(window.location);
    const k = url.searchParams.get("k");
    const c = url.searchParams.get("c");

    let categories = "";
    if (c !== null) {
      categories = url.searchParams.getAll("c")
        .map( i => "category2=" + i )
        .join("&");
    }

    const mode = k === null && c === null ? "form" : "result";

    let saved;
    try {
      saved = JSON.parse( window.sessionStorage.getItem("state") ) || {};
    }
    catch (err) {
      window.sessionStorage.removeItem("state");
      saved = {};
    };
    if (mode === "form") {
      saved.mode = "form";
    }

    this.state = {
      keyword: k || "",
      categories: categories,
      spots: [],
      offset: 0,
      hasNext: false,
      searching: false,
      searchError: null,
      mode: mode,
      hasResult: mode !== "form",
      ...saved
    };

    if (this.state.mode === "result") {
      this.startSearch(this.state.keyword, this.state.categories, true);
    }
  }

  /**
   * Component マウント時の処理
   */
  componentDidMount() {
    window.addEventListener("popstate", this.onPopState);
    if (this.state.mode !== "form" && this.state.spots.length === 0) {
      this.startSearch(this.state.keyword, this.state.categories);
    }

    // iPhone Safari で画面高さ 100vh が正常に取得できないので JavaScript で対
    // 処。ただし、これでも iPhone Safari のバグにより、画面下部にアドレスバー
    // 分の高さの余白がでることがある。（なお JavaScript を使わず、CSS の html,
    // body,... に height:100% を指定しても同じ症状が出る）
    const setFullHeight = () => {
      const vh = window.innerHeight;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    window.addEventListener("resize", setFullHeight);
    setFullHeight();
  }

  componentDidUpdate() {
    const state = this.state;
    window.sessionStorage.setItem("state", JSON.stringify(state));
  }

  /**
   * Component アンマウント時の処理
   */
  componentWillUnmount() {
    window.removeEventListener("popstate", this.onPopState);
  }

  /**
   * popstate のイベントリスナ
   */
  onPopState = e => {
    if (e.state === null) {
      this.setState({ mode: "form" });
    }
    else {
      if (e.state.mode === "result") {
        this.setState({ mode: "result" });
      }
      else if (e.state.mode == "map") {
        this.setState({ mode: "map" });
      }
      else {
        this.setState({ mode: "form" });
      }
    }
  }

  /**
   * this.state.mode を子コンポーネントから変更する。
   * @method
   * @param mode {string} - mode。
   */
  setMode = (mode) => {
    this.setState({ mode: mode });
  }

  /**
   * 検索結果画面に切り替え、[search()]{@link module:App~App#search} を呼び出し
   * て検索を開始する。
   *
   * @method
   * @param {string} keyword - 検索キーワード。
   *     [state]{@link module:App~App#state}.keyword に設定される。
   * @param {string} categories - 検索カテゴリパラメータ。
   *     [state]{@link module:App~App#state}.categories に設定される。
   */
  startSearch = (keyword, categories, noSearch) => {
    keyword = keyword.replace(/[\s\u3000]+/g, " ").trim();
    const state = {
      keyword: keyword,
      categories: categories,
      spots: [],
      offset: 0,
      hasNext: true,
      searching: true,
      mode: "result",
      searchError: null,
      hasResult: true
    };

    // pushState
    const url = new URL(window.location);
    url.searchParams.delete("k");
    url.searchParams.set("k", keyword);
    url.searchParams.delete("c");
    const cat = categories.split('&');
    if (cat.length > 0) {
      for (let i = 0; i < cat.length; i++) {
        const c = cat[i].substr(10);
        if (c !== "") {
          url.searchParams.append("c", cat[i].substr(10));
        }
      }
    }
    if (window.history.state === null) {
      window.history.pushState({ mode:"result", keyword: keyword, categories: categories }, '', url);
    }

    // search
    if (!noSearch) {
      this.setState(state);
      this.search(state);
    }
  }

  /**
   * [search()]{@link module:App~App#search} を呼び出して offset から先の検索の
   * 続きを行う。
   *
   * @method
   */
  nextSearch = () => {
    const state = this.state;
    if (state.searching) { // 検索処理中なので何もしない
      return;
    }
    const searchError = state.searchError;

    state.searchError = null;
    state.searching = true;
    this.setState(state);

    if (searchError) {
      // エラー後の再試行の場合は、連続して再試行されないように 1000ms の待ちを
      // 入れてから実行する。
      setTimeout( () => this.search( this.state ), 1000 );
    }
    else {
      this.search( this.state );
    }
  }

  /**
   * 検索を実行する。
   *
   * @method
   * @param state {Object} - 検索条件。
   *   [state]{@link module:App~App#state} がこの値に設定される。
   */
  search = (state) => {
    if (! state.hasNext) { // 全部検索済みなので何もしない
      return;
    }
    const keyword = state.keyword.replace(/[\s\u3000]+/g, " ").trim();
    const kwd = keyword !== ""
      ? keyword.split(/ /).map( k => "keyword=" + encodeURIComponent(k) ).join("&")
      : null;
    const cat = state.categories;
    const q1 = "offset=" + state.offset + "&limit=" + (KomaeConfig.KOKOSIL_SPOTS_LIMIT + 1);
    const q2 = kwd && cat ? "&" + kwd + "&" + cat
             : kwd        ? "&" + kwd
             : cat        ? "&" + cat
             : "";

    this.setState({ searcing: true });
    window.fetch( KomaeConfig.KOKOSIL_API + "/spots?" + q1 + q2 )
      .then(res => {
        if (!res.ok) {
          throw new Error(`Network Error ${res.status} ${res.statusText}`);
        }
        return res.json();
      })
      .then(data => {
        const spots = data["spots"];
        if (spots.length <= KomaeConfig.KOKOSIL_SPOTS_LIMIT) {
          this.setState({
            spots: this.state.spots.concat(spots),
            hasNext: false,
            searching: false
          });
        }
        else {
          this.setState({
            spots: this.state.spots.concat( spots.slice(0, KomaeConfig.KOKOSIL_SPOTS_LIMIT) ),
            offset: this.state.offset + KomaeConfig.KOKOSIL_SPOTS_LIMIT,
            hasNext: true,
            searching: false
          });
        }
      })
      .catch(error => {
        console.error("There has been a problem with your fetch operation: ", error);
        this.setState({
          searchError: error.message,
          searching: false
        });
      });
  }

  /**
   * トップレベル component の Render
   */
  render() {
    const mode           = this.state.mode;
    const formIsHidden   = mode === 'form' ? "" : " hidden";
    const resultIsHidden = mode !== 'form' ? "" : " hidden";
    return (
      <>
      <div id="form-panel" className={"container-fluid" + formIsHidden} >
      <KomaeForm
        startSearch={this.startSearch}
        keyword={this.state.keyword}
        categories={this.state.categories}
      />
      </div>
      <div id="result-panel" className={"container-fluid" + resultIsHidden} >
      <KomaeResult
        nextSearch={this.nextSearch}
        searchState={this.state}
        setMode={this.setMode}
      />
      </div>
      </>
    );
  }
}

export default App;
